import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { useMemo, useState } from "react";
import { ColumnDescription } from "react-bootstrap-table-next";
import Table from "../../../Components/Table";
import LeiaMais from "../../../Components/Utils/LeiaMais";
import { ListarProdutosReservadosController } from "../../../Controllers/Vendas/Produtos/GestaoReservas/GestaoReservasControllers";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { IProdutoReservado } from "../../../Interfaces/Vendas/Produtos/GestaoReservas/GestaoReservasInterfaces";
import FinalizarReserva from "./FinalizarReserva";

interface IGestaoReservasProps {}

export default function GestaoReservas(props: IGestaoReservasProps) {
    const [page, setPage] = useState(1);

    const {
        value: listarProdutosReservadosResponse,
        loading,
        execute: updateList,
    } = useAsyncEffect(async () => {
        return ListarProdutosReservadosController(page);
    }, [page]);

    const lstProdutosReservados =
        listarProdutosReservadosResponse?.PRODUTORESERVADO;
    const totalPages = listarProdutosReservadosResponse?.NR_PAGINAS ?? 1;

    const { dataColumns, tableViewColumns } = useMemo(
        () => getColumns(updateList),
        [updateList]
    );

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="mb-0">Produtos Reservados</h2>
            </div>
            <div className="card-body">
                <Table
                    data={lstProdutosReservados ?? []}
                    loading={loading}
                    bordered={false}
                    columns={dataColumns}
                    keyField="ID_RESERVA"
                    remote={{ pagination: true }}
                    onTableChange={(type, newState) => {
                        setPage(newState.page);
                    }}
                    totalPages={totalPages}
                    tableView={{
                        columns: tableViewColumns,
                        defaultClasses: "p-3",
                        defaultStyle: true,
                        cancelYScroll: true,
                    }}
                />
            </div>
        </div>
    );
}

function ProdutoReservado(
    props: IProdutoReservado & {
        updateList: () => void;
    }
) {
    return (
        <div className="card card-custom p-5 m-2">
            <div className=" d-flex flex-row flex-wrap g-4 justify-content-between align-items-start">
                <div className="d-flex flex-nowrap align-items-center justify-content-center">
                    <div className="symbol symbol-45 symbol-light-danger">
                        <div className="symbol-label">
                            <FontAwesomeIcon icon={faHome} size="lg" />
                        </div>
                    </div>
                    <div className="text-left ml-2">
                        <div className="font-size-h6 text-dark-75 font-weight-bolder">
                            {props.DS_PRODUTO_PAI}
                        </div>
                        <div className="font-size-sm font-weight-bold mt-1">
                            {props.DS_PRODUTO}
                        </div>
                    </div>
                </div>
                <div className="d-flex flex-wrap g-2">
                    <div className="mb-2 mr-5 d-flex-inline flex-column align-items-center">
                        <div className="d-block font-weight-bold mb-1 text-center">
                            Data de Envio
                        </div>
                        <div className="label label-light-primary label-inline my-1">
                            {moment(props.DT_RESERVA_INICIO).format("lll")}
                        </div>
                    </div>
                    <div className="mb-2 mr-5 d-flex-inline flex-column align-items-center">
                        <div className="d-block font-weight-bold mb-1 text-center">
                            Data de Expiração
                        </div>
                        <div className="label label-light-primary label-inline my-1">
                            {moment(props.DT_RESERVA_FIM).format("lll")}
                        </div>
                    </div>
                </div>
            </div>
            <div className="d-flex flex-wrap flex g-5 my-2">
                <div className="d-flex flex-column">
                    <span>Cliente</span>
                    <span className="font-weight-bolder font-size-h5">
                        {props.DS_NOME_RESERVA_CONTA}
                    </span>
                </div>
                <div className="d-flex flex-column">
                    <span>Gerente</span>
                    <span className="font-weight-bolder font-size-h5">
                        {props.DS_NOME_RESERVA_GERENTE}
                    </span>
                </div>
                <div className="d-flex flex-column">
                    <span>Corretor</span>
                    <span className="font-weight-bolder font-size-h5">
                        {props.DS_NOME_RESERVA_USUARIO}
                    </span>
                </div>
                <div className="d-flex flex-column">
                    <span>Equipe de Vendas</span>
                    <span className="font-weight-bolder font-size-h5">
                        {props.DS_NOME_RESERVA_EQUIPE_VENDAS}
                    </span>
                </div>
            </div>
            <hr />
            <div className="d-flex flex-column">
                <span className="font-weight-bolder font-size-h5">
                    Descrição da reserva
                </span>
                <span>
                    <LeiaMais charLimit={100}>{props.DS_RESERVA}</LeiaMais>
                </span>
            </div>
            <div className="d-flex justify-content-end mt-2">
                <FinalizarReserva
                    ID_PRODUTO={props.ID_PRODUTO}
                    ID_RESERVA_USUARIO={props.ID_RESERVA_USUARIO}
                    then={props.updateList}
                />
            </div>
        </div>
    );
}

function getColumns(updateList: () => void) {
    const dataColumns: Array<ColumnDescription<IProdutoReservado>> = [
        {
            dataField: "ID_PRODUTO",
            text: "Produtos Reservados",
            formatter: (c, row) => (
                <ProdutoReservado {...row} updateList={updateList} />
            ),
        },
    ];

    const tableViewColumns: Array<ColumnDescription<IProdutoReservado>> = [
        {
            dataField: "DS_PRODUTO_PAI",
            text: "Produto",
        },
        {
            dataField: "DS_PRODUTO",
            text: "Unidade",
        },
        {
            dataField: "DS_NOME_RESERVA_USUARIO",
            text: "Corretor",
        },
        {
            dataField: "DS_NOME_RESERVA_GERENTE",
            text: "Gerente",
        },
        {
            dataField: "DS_NOME_RESERVA_EQUIPE_VENDAS",
            text: "Equipe de Vendas",
        },
        {
            dataField: "DS_NOME_RESERVA_CONTA",
            text: "Cliente",
        },
        {
            dataField: "DS_RESERVA",
            text: "Descrição da Reserva",
            formatter: (c) => <LeiaMais charLimit={50}>{c}</LeiaMais>,
        },
        {
            dataField: "DT_RESERVA_INICIO",
            text: "Data da Reserva",
            formatter: (c) => new Date(c).toLocaleString(),
        },
        {
            dataField: "DT_RESERVA_FIM",
            text: "Data de Expiração",
            formatter: (c) => new Date(c).toLocaleString(),
        },
        {
            dataField: "",
            text: "Finalizar Reserva",
            formatter: (_, row) => (
                <FinalizarReserva
                    ID_PRODUTO={row.ID_PRODUTO}
                    then={updateList}
                    ID_RESERVA_USUARIO={row.ID_RESERVA_USUARIO}
                />
            ),
            isDummyField: true,
        },
    ];

    return {
        dataColumns,
        tableViewColumns,
    };
}
